import axios from "axios";
import services from "@/services/index.js";

export default {

    postPatient: async (body) => {
        return await services.postAsync({
            url: `/patients`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    postOwner: async (body) => {
        return await services.postAsync({
            url: `/patients/owners`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    getOwnerById: async (id) => {
        return await services.getAsync({
            url: `/patients/owners/${id}`,
            timeout: 10000,
            hasToken: true
        });
    },

    getPatients: async (search) => {
        return await services.getAsync({
            url: `/patients?search=${search}`,
            timeout: 10000,
            hasToken: true
        });
    },

    getPatientById: async (id) => {
        return await services.getAsync({
            url: `/patients?id=${encodeURIComponent(id)}`,
            timeout: 10000,
            hasToken: true
        });
    },

    getOwners: async (search) => {
        return await services.getAsync({
            url: `/patients/owners?search=${search}`,
            timeout: 10000,
            hasToken: true
        });
    },

    getPatientShareById: async (id) => {
        return await services.getAsync({
            url: `/patients/shares/${id}`,
            timeout: 20000,
            hasToken: false
        });
    },

    getSharedClinicalHandoutFromId: async (handoutId, shareId) => {
        return await services.getAsync({
            url: `/patients/handouts/${handoutId}?shareId=${shareId}`,
            timeout: 20000,
            hasToken: false
        });
    },

    saveEncounter: async (body) => {
        return await services.postAsync({
            url: `/patients/encounters`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    getEncounters: async (patientId, cursor) => {
        let url = `/patients/encounters?patientId=${encodeURIComponent(patientId)}&pagination=v2`;
        if (cursor) {
            url += `&cursor=${encodeURIComponent(cursor)}`;
        }
        return await services.getAsync({
            url,
            timeout: 20000,
            hasToken: true
        });
    },

    getEncounterById: async (id) => {
        return await services.getAsync({
            url: `/patients/encounters/${id}`,
            timeout: 20000,
            hasToken: true
        });
    },

    deleteEncounter: async (id) => {
        return await services.deleteAsync({
            url: `/patients/encounters/${id}`,
            timeout: 10000,
            hasToken: true
        });
    },

    saveAnamnesis: async (encounterId, body) => {
        return await services.postAsync({
            url: `/patients/encounters/${encounterId}/anamnesis`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    getAnamnesis: async (encounterId) => {
        return await services.getAsync({
            url: `/patients/encounters/${encounterId}/anamnesis`,
            timeout: 10000,
            hasToken: true
        });
    },

    savePhysicalExam: async (encounterId, body) => {
        return await services.postAsync({
            url: `/patients/encounters/${encounterId}/physical-exam`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    getPhysicalExam: async (encounterId) => {
        return await services.getAsync({
            url: `/patients/encounters/${encounterId}/physical-exam`,
            timeout: 10000,
            hasToken: true
        });
    },

    saveDiagnoses: async (encounterId, body) => {
        return await services.postAsync({
            url: `/patients/encounters/${encounterId}/diagnoses?response=v2`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    getDiagnoses: async (encounterId) => {
        return await services.getAsync({
            url: `/patients/encounters/${encounterId}/diagnoses`,
            timeout: 10000,
            hasToken: true
        });
    },

    saveVaccination: async (body) => {
        return await services.postAsync({
            url: `/patients/vaccinations`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    getVaccinations: async (patientId, cursor) => {
        return await services.getAsync({
            url: `/patients/vaccinations?patientId=${encodeURIComponent(patientId)}&pagination=v2${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
            timeout: 20000,
            hasToken: true
        });
    },

    getVaccinationSchedule: async (patientId) => {
        return await services.getAsync({
            url: `/patients/vaccinations/schedule?patientId=${patientId}`,
            timeout: 10000,
            hasToken: true
        });
    },

    deleteVaccination: async (vaccinationId) => {
        return await services.deleteAsync({
            url: `/patients/vaccinations/${vaccinationId}`,
            timeout: 10000,
            hasToken: true
        });
    },

    saveLabExam: async (body) => {
        return await services.postAsync({
            url: `/patients/lab-exams`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    getLabExams: async (patientId, cursor) => {
        return await services.getAsync({
            url: `/patients/lab-exams?patientId=${encodeURIComponent(patientId)}&pagination=v2${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
            timeout: 20000,
            hasToken: true
        });
    },

    uploadLabExamFile: async (labExamId, body) => {
        return await services.postAsync({
            url: `/patients/lab-exams/${labExamId}/files`,
            body,
            timeout: 30000,
            hasToken: true
        });
    },

    deleteLabExam: async (labExamId) => {
        return await services.deleteAsync({
            url: `/patients/lab-exams/${labExamId}`,
            timeout: 10000,
            hasToken: true
        });
    },

    saveHospitalization: async (body) => {
        return await services.postAsync({
            url: `/patients/hospitalizations`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    getHospitalizations: async (patientId, cursor) => {
        return await services.getAsync({
            url: `/patients/hospitalizations?patientId=${encodeURIComponent(patientId)}&pagination=v2${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
            timeout: 20000,
            hasToken: true
        });
    },

    addEvolution: async (hospitalizationId, body) => {
        return await services.postAsync({
            url: `/patients/hospitalizations/${hospitalizationId}/evolutions`,
            body,
            timeout: 10000,
            hasToken: true
        });
    },

    deleteHospitalization: async (hospitalizationId) => {
        return await services.deleteAsync({
            url: `/patients/hospitalizations/${hospitalizationId}`,
            timeout: 10000,
            hasToken: true
        });
    },

    lookupCep: async (cep) => {
        const sanitized = (cep || '').replace(/[^0-9]/g, '');
        if (sanitized.length !== 8) return null;
        try {
            const { data } = await axios.get(
                `https://viacep.com.br/ws/${sanitized}/json/`,
                { timeout: 8000 }
            );
            if (!data || data.erro) return null;
            return {
                addressState: data.uf || '',
                addressCity: data.localidade || '',
                addressNeighborhood: data.bairro || '',
                addressStreet: data.logradouro || ''
            };
        } catch {
            return null;
        }
    }
}
